import React from 'react';
import { send } from '../../assets/images/image';
import ReCAPTCHA from "react-google-recaptcha";
import { toast } from 'react-toastify';
import { motion } from 'framer-motion';
import { API_URL, RECAPTCHA_KEY } from '../../configs/default.config';

const fadeInUp = {
    hidden: { y: 20, opacity: 0 },
    visible: {
        y: 0,
        opacity: 1,
        transition: {
            staggerChildren: 0.2,
            delayChildren: 0.3,
        }
    }
};

const ContactMobile: React.FC = () => {
    const recaptchaRef = React.useRef<ReCAPTCHA | null>(null);
    const [loading, setLoading] = React.useState(false);
    const [captchaToken, setCaptchaToken] = React.useState<string | null>(null);
    const [formData, setFormData] = React.useState({
        name: '',
        email: '',
        message: '',
    });

    const handleSubmtit = async () => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!formData.name || !formData.email || !formData.message) {
            toast.error("Please fill out all required fields.");
            return;
        }

        if (!emailRegex.test(formData.email)) {
            toast.error("Invalid email format.");
            return;
        }

        if (!captchaToken) {
            toast.error("Please complete the CAPTCHA.");
            return;
        }
        setLoading(true);
        try {
            const response = await fetch(API_URL, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    ...formData,
                    recaptchaToken: captchaToken,
                }),
            });
            if (!response.ok) throw new Error("Failed to send message.");
            toast.success("Message sent successfully");
            setFormData({
                name: '',
                email: '',
                message: '',
            });
            recaptchaRef.current?.reset();
            setCaptchaToken(null);
            setLoading(false);
        } catch (error) {
            toast.error("Something went wrong. Please try again.");
            setLoading(false);
        }
    };

    return (
        <>
            <motion.div
                className="w-full px-6 text-center"
                variants={fadeInUp}
                initial="hidden"
                animate="visible"
                custom={0}
            >
                <div className='text-[42px] font-light leading-none text-[#494949]'>
                    We're Here
                </div>
            </motion.div>

            <motion.div
                className="w-full px-6 text-center mt-[16px]"
                variants={fadeInUp}
                initial="hidden"
                animate="visible"
                custom={1}
            >
                <div className='inline-flex px-5 justify-center items-center shrink-0 bg-[#3A57FF] text-[#FAFAFA] text-[42px] font-extrabold'>
                    to Help
                </div>
            </motion.div>

            {/* Name */}
            <motion.div
                className="w-full px-6 text-center mt-[100px] flex flex-col"
                variants={fadeInUp}
                initial="hidden"
                animate="visible"
                custom={2}
            >
                <div className="flex flex-row w-full text-[#494949] text-base font-bold">
                    <div className="inline-flex px-2 justify-center items-center gap-4 shrink-0">
                        Your Name
                    </div>
                    <div className="inline-flex shrink-0 bg-[#3A57FF] text-[#FAFAFA] text-base font-[400] h-7">
                        (Required*)
                    </div>
                </div>
                <input
                    type="text"
                    className="w-full h-[52px] px-4 py-4 gap-4 rounded-[24px] bg-[rgba(0,0,0,0.15)] mt-[18px] mb-[16px]
                            placeholder:italic placeholder:text-[#888]
                            focus:outline-none focus:ring-2 focus:ring-[#3A57FF]"
                    placeholder='Enter your name'
                    value={formData.name}
                    onChange={(e) =>
                        setFormData({ ...formData, name: e.target.value })
                    }
                />
            </motion.div>

            {/* Email */}
            <motion.div
                className="w-full px-6 text-center mt-[16px] flex flex-col"
                variants={fadeInUp}
                initial="hidden"
                animate="visible"
                custom={3}
            >
                <div className="flex flex-row w-full text-[#494949] text-base font-bold">
                    <div className="inline-flex px-2 justify-center items-center gap-4 shrink-0">
                        Your Email
                    </div>
                    <div className="inline-flex shrink-0 bg-[#3A57FF] text-[#FAFAFA] text-base font-[400] h-7">
                        (Required*)
                    </div>
                </div>
                <input
                    type="text"
                    className="w-full h-[52px] px-4 py-4 gap-4 rounded-[24px] bg-[rgba(0,0,0,0.15)] mt-[18px] mb-[16px]
                            placeholder:italic placeholder:text-[#888]
                            focus:outline-none focus:ring-2 focus:ring-[#3A57FF]"
                    placeholder="Enter your email address"
                    value={formData.email}
                    onChange={(e) =>
                        setFormData({ ...formData, email: e.target.value })
                    }
                />
            </motion.div>

            {/* Message */}
            <motion.div
                className="w-full px-6 text-center mt-[16px] flex flex-col"
                variants={fadeInUp}
                initial="hidden"
                animate="visible"
                custom={4}
            >
                <div className="flex flex-row w-full text-[#494949] text-base font-bold">
                    <div className="inline-flex px-2 justify-center items-center gap-4 shrink-0">
                        Your Message
                    </div>
                    <div className="inline-flex shrink-0 bg-[#3A57FF] text-[#FAFAFA] text-base font-[400] h-7">
                        (Required*)
                    </div>
                </div>
                <textarea
                    className="w-full rounded-[24px] bg-[rgba(0,0,0,0.15)] mt-[18px] mb-[32px] px-4 py-4 placeholder:italic placeholder:text-[#888] resize-none focus:outline-none focus:ring-2 focus:ring-[#3A57FF]"
                    placeholder="Enter your message"
                    rows={8}
                    value={formData.message}
                    onChange={(e) =>
                        setFormData({ ...formData, message: e.target.value })
                    }
                />
            </motion.div>

            {/* CAPTCHA */}
            <motion.div
                className="w-full px-6 text-center mt-[10px] flex flex-col"
                variants={fadeInUp}
                initial="hidden"
                animate="visible"
                custom={5}
            >
                <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_KEY}
                    onChange={(token) => setCaptchaToken(token)}
                    onExpired={() => setCaptchaToken(null)}
                    size="normal"
                />
            </motion.div>

            {/* Submit */}
            <motion.div
                className="w-full px-6 text-center mt-[40px] flex flex-col"
                variants={fadeInUp}
                initial="hidden"
                animate="visible"
                custom={6}
            >
                <button
                    onClick={handleSubmtit}
                    disabled={loading}
                    className="w-full inline-flex h-[53px] px-8 py-4 justify-center items-center gap-2 flex-shrink-0 rounded-[16px] bg-[#3A57FF] text-[17px] font-normal leading-[normal] text-[#FAFAFA]">
                    <span className="flex items-center justify-center mb-1">{loading ? "Sending..." : "Submit"}&nbsp;</span>
                    <img
                        src={send}
                        alt="Task automation mockup"
                        className="h-[24px]"
                    />
                </button>
            </motion.div>
        </>
    );
};

export default ContactMobile;
